import { useEffect, useState } from 'react';

import { Database } from '~/services/db';
import { ClientDatabase } from '~/types/client';
import type { AiPlaylistOption } from '~/config/blindTestParams.config';

/**
 * Playlists IA du client pour le cockpit (Blind Test uniquement). Lues dans
 * Firestore et re-lues à chaque changement (live-sync), pour qu'une playlist
 * créée sur l'écran devienne immédiatement sélectionnable ici, et disparaisse
 * de la liste si elle est supprimée.
 *
 * Gaté sur le flag de rollout `features.playlistPersistence` (suivi en temps
 * réel) : sans le flag, le jeu ne connaît pas ces playlists — les proposer ici
 * enverrait un ID que l'écran ne peut pas résoudre (chargement infini). Des
 * clients ont des docs `aiPlaylists` résiduels de juillet 2026 : ils ne
 * doivent réapparaître qu'une fois le client enrôlé.
 */
export function useAiPlaylists(clientID: string, gameID: string): AiPlaylistOption[] {
    const [playlists, setPlaylists] = useState<AiPlaylistOption[]>([]);
    const [enabled, setEnabled] = useState(false);

    useEffect(() => {
        setEnabled(false);
        if (gameID !== 'unblind-test' || !clientID) return undefined;
        const unsubscribe = ClientDatabase.subscribeFeature(clientID, 'playlistPersistence', setEnabled);
        return () => unsubscribe();
    }, [clientID, gameID]);

    useEffect(() => {
        if (gameID !== 'unblind-test' || !clientID || !enabled) {
            setPlaylists([]);
            return undefined;
        }

        const load = () => {
            Database.playlists
                .retrievePlaylists(clientID, 'unblind-test', 0)
                .then((remote) => {
                    setPlaylists(remote.map((p) => ({ id: p.id, name: p.name, trackCount: p.tracks.length })));
                })
                .catch((error) => {
                    console.error('Error loading AI playlists for cockpit:', error);
                });
        };

        const unsubscribe = Database.playlists.subscribeToPlaylists(clientID, 'unblind-test')(load);
        return () => unsubscribe();
    }, [clientID, gameID, enabled]);

    return playlists;
}
